/**
 * Content Manager - Audit Service
 *
 * Service layer for audit log operations using AWS API Gateway.
 */

import { apiClient, isAWSConfigured } from '../lib/awsConfig';
import type { AuditLog, ApiResponse, PaginatedResponse } from '../types';

// =============================================================================
// AUDIT SERVICE
// =============================================================================

export const auditService = {
  /**
   * Fetch audit logs with pagination
   */
  async fetch(
    page = 1,
    pageSize = 50,
    filters?: {
      tableName?: string;
      recordId?: string;
      action?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<PaginatedResponse<AuditLog>> {
    if (!isAWSConfigured()) {
      return { data: [], total: 0, page, pageSize, hasMore: false };
    }

    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('pageSize', pageSize.toString());

      if (filters?.tableName) params.set('tableName', filters.tableName);
      if (filters?.recordId) params.set('recordId', filters.recordId);
      if (filters?.action) params.set('action', filters.action);
      if (filters?.userId) params.set('userId', filters.userId);
      if (filters?.startDate) params.set('startDate', filters.startDate);
      if (filters?.endDate) params.set('endDate', filters.endDate);

      const { data, error } = await apiClient.get<PaginatedResponse<AuditLog>>(
        `/audit?${params.toString()}`
      );

      if (error) throw error;

      return data || { data: [], total: 0, page, pageSize, hasMore: false };
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      return { data: [], total: 0, page, pageSize, hasMore: false };
    }
  },

  /**
   * Fetch audit history for a specific record
   */
  async fetchByRecordId(
    tableName: string,
    recordId: string,
    limit = 100
  ): Promise<ApiResponse<AuditLog[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    try {
      const { data, error } = await apiClient.get<AuditLog[]>(
        `/audit/record/${tableName}/${recordId}?limit=${limit}`
      );

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching record audit history:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch recent audit logs
   */
  async fetchRecent(limit = 20): Promise<ApiResponse<AuditLog[]>> {
    if (!isAWSConfigured()) {
      return { data: [], error: null };
    }

    try {
      const { data, error } = await apiClient.get<AuditLog[]>(
        `/audit/recent?limit=${limit}`
      );

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Error fetching recent audit logs:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Fetch audit logs by user
   */
  async fetchByUserId(
    userId: string,
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<AuditLog>> {
    return this.fetch(page, pageSize, { userId });
  },

  /**
   * Fetch audit logs by table
   */
  async fetchByTable(
    tableName: string,
    page = 1,
    pageSize = 50
  ): Promise<PaginatedResponse<AuditLog>> {
    return this.fetch(page, pageSize, { tableName });
  },

  /**
   * Get audit statistics
   */
  async getStats(): Promise<ApiResponse<{
    totalLogs: number;
    byAction: Record<string, number>;
    byTable: Record<string, number>;
    recentActivityCount: number;
  }>> {
    if (!isAWSConfigured()) {
      return {
        data: {
          totalLogs: 0,
          byAction: {},
          byTable: {},
          recentActivityCount: 0,
        },
        error: null,
      };
    }

    try {
      const { data, error } = await apiClient.get<{
        totalLogs: number;
        byAction: Record<string, number>;
        byTable: Record<string, number>;
        recentActivityCount: number;
      }>('/audit/stats');

      if (error) throw error;

      return {
        data: data || {
          totalLogs: 0,
          byAction: {},
          byTable: {},
          recentActivityCount: 0,
        },
        error: null,
      };
    } catch (error) {
      console.error('Error fetching audit stats:', error);
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get the diff between old and new data
   */
  getDiff(oldData: Record<string, unknown> | null, newData: Record<string, unknown> | null): Array<{
    field: string;
    oldValue: unknown;
    newValue: unknown;
  }> {
    if (!oldData && !newData) return [];
    if (!oldData) {
      return Object.entries(newData!).map(([field, newValue]) => ({
        field,
        oldValue: undefined,
        newValue,
      }));
    }
    if (!newData) {
      return Object.entries(oldData).map(([field, oldValue]) => ({
        field,
        oldValue,
        newValue: undefined,
      }));
    }

    const allKeys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);
    const changes: Array<{ field: string; oldValue: unknown; newValue: unknown }> = [];

    allKeys.forEach((field) => {
      const oldValue = oldData[field];
      const newValue = newData[field];

      if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
        changes.push({ field, oldValue, newValue });
      }
    });

    return changes;
  },
};

export default auditService;
